import React, { useCallback, useEffect, useMemo, useState } from 'react';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { useLocalStorage } from '@atlaskit/frontend-utilities';
import { fg } from '@atlaskit/platform-feature-flags';
import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics';
import { type User as UserPickerUser } from '@atlaskit/user-picker';
import { LazyIntlProvider } from '@atlassian/teams-common/i18n';
import { useTeamsPermissionsService } from '@atlassian/teams-common/use-teams-permissions';

import { postCreateTeamContainers } from '../../clients/team-client';
import { useInviteAndAddToTeam } from '../../hooks/use-invite-and-add-to-team';
import { useInviteCapabilities } from '../../hooks/use-invite-capabilities';
import useTeamCreateDialogAnalytics from '../../hooks/use-team-create-dialog-analytics';
import { TeamMembershipSettings } from '../../types';
import { type InvitedUser } from '../../types/user';
import { categoriseSelectedMembers } from '../../utils/categorise-selected-members';
import { MAX_MEMBERS, VOID_FUNC } from '../../utils/constants';
import { getInviteProducts } from '../../utils/get-invite-products';
import { validateTeamName } from '../../utils/validators';
import { haveISeenContainersKey } from '../CreateContainer';
import { ContainerType } from '../CreateContainer/types';
import { fetchLanguageFile } from '../i18n/utils';
import { type TeamTypeValue } from '../TeamTypeSelect/TeamTypeSelect';

import Form from './Form';
import { type TeamCreateProps } from './types';
import { useContainerPermissionsAnalyticsEvent } from './useContainerPermissionsAnalyticsEvent';
import useTeamCreateMutation from './useTeamCreateMutation';

function TeamCreate(props: TeamCreateProps) {
	const {
		proposedMembers = [],
		cloudId,
		maxSelectedMembers = MAX_MEMBERS,
		extraAnalyticsAttrs,
		enableMembershipSettingsChoice,
		initialMembershipSettings = enableMembershipSettingsChoice
			? TeamMembershipSettings.INVITE_ONLY
			: TeamMembershipSettings.OPEN,
		setIsLoading = VOID_FUNC,
		onClose = VOID_FUNC,
		customMessages = {},
		customComponents = {},
		currentUser,
		orgId,
		enableTeamPermissionCheck,
		prepopulatedFields = {},
		onGoBack,
		hideCreateContainers = false,
		headless = false,
		onCreateClicked,
	} = props;

	const { fireEvent } = useAnalyticsEvents();
	const {
		triggerAnalyticsForSubmitTeamCreateDialog: triggerAnalyticsForSubmitTeamCreateDialogNext,
	} = useTeamCreateDialogAnalytics();
	const isEligibleProductForInvites = props.product === 'jira' || props.product === 'atlas';
	const { canInviteToJiraSoftware, canInviteToConfluence } = useInviteCapabilities({
		cloudId,
		shouldRun: isEligibleProductForInvites && fg('jira_team_experience_improvements_feature_gate'),
	});

	const isTeamExperienceImprovementEnabled =
		isEligibleProductForInvites &&
		canInviteToJiraSoftware &&
		FeatureGates.initializeCalled() &&
		FeatureGates.getExperimentValue('jira_team_experience_improvements', 'isEnabled', false);

	const inviteAndAddToTeam = useInviteAndAddToTeam({
		products: getInviteProducts({
			product: props.product,
			canInviteToJiraSoftware,
			canInviteToConfluence,
		}),
		originProduct: props.product,
	});
	const [_, setHaveISeenContainers] = useLocalStorage<boolean>(haveISeenContainersKey, false);

	const showCreateContainers = useMemo(() => {
		if (hideCreateContainers) {
			return false;
		}

		if (!currentUser?.id) {
			return false;
		}

		if (FeatureGates.initializeCalled()) {
			return FeatureGates.getExperimentValue(
				'teams_app_auto_container_create_universal_create',
				'isEnabled',
				false,
				{
					fireExperimentExposure: false,
				},
			);
		}

		return false;
	}, [currentUser?.id, hideCreateContainers]);

	const [containers, setCreateContainers] = useState<ContainerType[]>([]);

	const onCreateTeamSuccess: NonNullable<TeamCreateProps['onCreateTeamSuccess']> = async (
		teamData,
		invitedMembers,
		failedInvitedMembers,
	) => {
		let requestedContainers: ContainerType[] = [];

		if (teamData.id && containers.length > 0 && showCreateContainers) {
			setHaveISeenContainers(true);
			const payload = containers.map((container) => ({
				type: container,
				containerSiteId: cloudId,
			}));
			/*
				This is not be awaited due to performance reasons.
				These requests can take multiple seconds, so we don't want to block the UI.
			*/
			void postCreateTeamContainers({ teamId: teamData.id, containers: payload });
			requestedContainers = payload.map((container) => container.type);
			fireEvent('track.requestedContainers.requested', {
				containers: Object.values(ContainerType).reduce(
					(acc, type) => {
						acc[type] = containers?.includes(type) ?? false;
						return acc;
					},
					{} as Record<string, boolean>,
				),
				...extraAnalyticsAttrs,
			});
		}

		if (isTeamExperienceImprovementEnabled) {
			const { newUsersWhoNeedInvite } = categoriseSelectedMembers(selectedMembers);
			// This is similarly not awaited for performance reasons.
			void inviteAndAddToTeam({ newUsersWhoNeedInvite, cloudId, teamId: teamData.id, orgId });
		}

		if (props?.onCreateTeamSuccess) {
			await props.onCreateTeamSuccess(
				teamData,
				invitedMembers,
				failedInvitedMembers,
				requestedContainers,
			);
		}
		return { requestedContainers };
	};

	const { isFetching: mutationIsLoading, createTeamAndAddMembers } = useTeamCreateMutation({
		...props,
		onCreateTeamSuccess,
	});

	const {
		members: proposedMembersNext,
		membershipSettings: initialMembershipSettingsNext,
		teamName: initialTeamName,
	} = prepopulatedFields;

	const [teamName, setTeamName] = useState(initialTeamName || '');
	const [isFetching, setIsFetching] = useState(false);
	const [membershipSettings, setMembershipSettings] = useState(
		initialMembershipSettingsNext ? initialMembershipSettingsNext : initialMembershipSettings,
	);
	const [selectedMembers, setSelectedMembers] = useState<InvitedUser[]>([]);
	const [suggestedMembers, setSuggestedMembers] = useState<InvitedUser[]>([]);
	const [isSubmittedOnce, setIsSubmittedOnce] = useState(false);
	const [isMemberPickerInvalid, setIsMemberPickerInvalid] = useState(false);
	const [isCustomInputFieldInvalid, setIsCustomInputFieldInvalid] = useState(true);

	useContainerPermissionsAnalyticsEvent(cloudId, currentUser?.id);

	const { loading, permissions } = useTeamsPermissionsService(
		{ orgId, cloudId },
		{
			enabled: !!enableTeamPermissionCheck,
			attributes: {
				userId: currentUser?.id,
			},
		},
	);
	// When team type is enabled, we want to hide the Membership Control by default for admins, to avoid the UI friction while loading types options
	const [showTeamTypePickerState, setShowTeamTypePickerState] = useState(
		fg('enable_atlassian_team_types_m1') ? false : Boolean(enableMembershipSettingsChoice),
	);
	const [showTeamTypeSelect, setShowTeamTypeSelect] = useState(false);
	const [teamType, setTeamType] = useState<string | undefined>();

	const proposedMembersWithCurrentUser = useMemo(() => {
		const members = [
			...(proposedMembersNext ? proposedMembersNext : proposedMembers),
			...(currentUser && currentUser.id ? [currentUser] : []),
		];
		const uniqueMembers = members.filter(
			(member, index, self) => index === self.findIndex((m) => m.id === member.id),
		);
		return uniqueMembers;
	}, [proposedMembersNext, proposedMembers, currentUser]);

	// When team permission is loaded, we decide to render these or not based on permissions.
	useEffect(() => {
		if (!loading && permissions && fg('enable_atlassian_team_types_m1')) {
			setShowTeamTypePickerState(
				!permissions.canAdminTeams && Boolean(enableMembershipSettingsChoice),
			);
			setShowTeamTypeSelect(Boolean(permissions.canAdminTeams));
		}
	}, [loading, permissions, enableMembershipSettingsChoice]);

	// did mount
	useEffect(() => {
		setSelectedMembers(proposedMembersWithCurrentUser);

		fireEvent('screen.teamCreateDialog.viewed', {
			proposedMembersLength: (proposedMembersNext ? proposedMembersNext : proposedMembers).length,
			...extraAnalyticsAttrs,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		setIsFetching(!!mutationIsLoading);
		setIsLoading(!!mutationIsLoading || loading);

		// when isFetching prop change from true --> false, that means the creating team finishes
		if (mutationIsLoading === false && isFetching === true) {
			onClose();
		}
	}, [mutationIsLoading, isFetching, setIsLoading, onClose, loading]);

	const handleSelectedChange = useCallback(
		(users: UserPickerUser[], data: { hasError: boolean; isDisabled: boolean }) => {
			setSelectedMembers(
				users.map((user) => ({
					id: user.id,
					email: user.email,
					fullName: user.name,
					avatarUrl: user.avatarUrl,
				})),
			);

			setSuggestedMembers((existingSuggestedMembers) =>
				existingSuggestedMembers.filter((suggestedMember) =>
					users.find((user) => user.id === suggestedMember.id),
				),
			);

			setIsMemberPickerInvalid(data.hasError || users.length === 0);
		},
		[setIsMemberPickerInvalid, setSelectedMembers],
	);

	const handleChange = useCallback(
		(customInput: any) => {
			const isInvalid = !customInput || !customInput.value;
			setIsCustomInputFieldInvalid(isInvalid);
		},
		[setIsCustomInputFieldInvalid],
	);

	const handleSuggestedMemberSelection = useCallback(
		(newValue: InvitedUser) => {
			fireEvent('ui.member.suggested', {
				...extraAnalyticsAttrs,
			});
			setSuggestedMembers((existingSuggestedMembers) => [...existingSuggestedMembers, newValue]);
		},
		[extraAnalyticsAttrs, setSuggestedMembers, fireEvent],
	);

	const handleTeamTypeSelectChange = useCallback(
		(value: TeamTypeValue) => {
			// Hide the Team Type Select when no value, which means there was no team type at all exists in the user base
			if (!value.id) {
				setShowTeamTypeSelect(false);
				setTeamType(undefined);
			} else {
				setTeamType(value.id);
			}
			// Show the MEMBERSHIP CONTROL section when the team type is not verified
			setShowTeamTypePickerState(!value.verified);

			if (value.verified) {
				setMembershipSettings(TeamMembershipSettings.ORG_ADMIN_MANAGED);
			} else {
				setMembershipSettings(
					initialMembershipSettingsNext ? initialMembershipSettingsNext : initialMembershipSettings,
				);
			}
		},
		[
			setShowTeamTypeSelect,
			setShowTeamTypePickerState,
			initialMembershipSettingsNext,
			initialMembershipSettings,
		],
	);

	const canTeamBeCreated = useCallback(() => {
		const teamNameValidationError = validateTeamName(teamName);
		const hasCustomField = Boolean(customComponents?.customInputField);
		const result =
			!isMemberPickerInvalid &&
			!teamNameValidationError &&
			(!hasCustomField || !isCustomInputFieldInvalid) &&
			!isFetching;
		return result;
	}, [
		isFetching,
		isMemberPickerInvalid,
		isCustomInputFieldInvalid,
		teamName,
		customComponents?.customInputField,
	]);

	const handleSubmit = useCallback(() => {
		triggerAnalyticsForSubmitTeamCreateDialogNext({
			canCreateTeam: canTeamBeCreated(),
			numberOfMembers: selectedMembers.length,
			numberOfSuggestedMembers: suggestedMembers.length,
			...extraAnalyticsAttrs,
		});

		setIsSubmittedOnce(true);
		onCreateClicked?.({ teamName, selectedMembers, membershipSettings });

		if (!canTeamBeCreated()) {
			const teamNameValidationError = validateTeamName(teamName);
			return {
				teamName: teamNameValidationError,
				inviteMembers: selectedMembers.length === 0,
			};
		}
		if (canTeamBeCreated()) {
			setIsFetching(true);
			// outsource creating a new team + invite members logic to outside
			// to reduce complexity of current file
			createTeamAndAddMembers(teamName, selectedMembers, membershipSettings, teamType);
		}
	}, [
		onCreateClicked,
		teamName,
		selectedMembers,
		membershipSettings,
		canTeamBeCreated,
		triggerAnalyticsForSubmitTeamCreateDialogNext,
		suggestedMembers.length,
		extraAnalyticsAttrs,
		createTeamAndAddMembers,
		teamType,
	]);

	const handleSubmitForExistingUsers = useCallback(async () => {
		triggerAnalyticsForSubmitTeamCreateDialogNext({
			canCreateTeam: canTeamBeCreated(),
			numberOfMembers: selectedMembers.length,
			numberOfSuggestedMembers: suggestedMembers.length,
			...extraAnalyticsAttrs,
		});

		setIsSubmittedOnce(true);
		onCreateClicked?.({ teamName, selectedMembers, membershipSettings });

		if (!canTeamBeCreated()) {
			const teamNameValidationError = validateTeamName(teamName);
			return {
				teamName: teamNameValidationError,
				inviteMembers: selectedMembers.length === 0,
			};
		}

		const { existingUsers } = categoriseSelectedMembers(selectedMembers);

		setIsFetching(true);
		createTeamAndAddMembers(teamName, existingUsers, membershipSettings, teamType);
	}, [
		canTeamBeCreated,
		createTeamAndAddMembers,
		extraAnalyticsAttrs,
		membershipSettings,
		selectedMembers,
		suggestedMembers,
		teamName,
		triggerAnalyticsForSubmitTeamCreateDialogNext,
		onCreateClicked,
		teamType,
	]);

	return (
		<Form
			headless={headless}
			handleSubmit={
				isTeamExperienceImprovementEnabled ? handleSubmitForExistingUsers : handleSubmit
			}
			onClose={onClose}
			isFetching={isFetching}
			canCreateTeams={permissions.canCreateTeams}
			maxSelectedMembers={maxSelectedMembers}
			customMessages={customMessages}
			customComponents={customComponents}
			showTeamTypePicker={showTeamTypePickerState}
			showTeamTypeSelect={showTeamTypeSelect}
			showCreateContainers={showCreateContainers}
			onGoBack={onGoBack}
			handleSuggestedMemberSelection={handleSuggestedMemberSelection}
			cloudId={cloudId}
			currentUserId={currentUser?.id}
			teamNameInputProps={{
				onChange: setTeamName,
				onSubmit: handleSubmit,
				isSubmittedOnce,
				initialTeamName,
			}}
			memberPickerProps={(error) => ({
				cloudId,
				onChange: handleSelectedChange,
				initialValues: proposedMembersWithCurrentUser,
				maxSelectedMembers,
				extraAnalyticsAttrs,
				showHelperMessages: false,
				isInvalid: isSubmittedOnce ? isMemberPickerInvalid : !!error,
				showErrorMessage: Boolean(isSubmittedOnce ? isMemberPickerInvalid : error),
				currentUserEmail: currentUser?.email,
				allowEmailInvites: isTeamExperienceImprovementEnabled,
				bootstrapOptions: isTeamExperienceImprovementEnabled ? undefined : [],
			})}
			customInputFieldProps={(error) => {
				const props = {
					orgId: orgId || '',
					isRequired: true,
					isInvalid: isSubmittedOnce ? isCustomInputFieldInvalid : !!error,
					showErrorMessage: Boolean(isSubmittedOnce ? isCustomInputFieldInvalid : error),
					onChange: handleChange,
				};
				return props;
			}}
			teamTypePickerProps={{
				onChange: setMembershipSettings,
				defaultValue: initialMembershipSettingsNext
					? initialMembershipSettingsNext
					: initialMembershipSettings,
			}}
			teamTypeSelectProps={{
				cloudId,
				onChange: handleTeamTypeSelectChange,
			}}
			createContainersProps={{
				cloudId,
				//verified userId is truthy in showCreateContainers
				userId: currentUser?.id as string,
				onChange: setCreateContainers,
			}}
		/>
	);
}

const TeamCreateWithLazyIntlProvider = (props: TeamCreateProps) => {
	return (
		<LazyIntlProvider fetchLanguageFile={fetchLanguageFile}>
			<TeamCreate {...props} />
		</LazyIntlProvider>
	);
};

export default TeamCreateWithLazyIntlProvider;
